// src/resources/customerLifecycle/LifecycleCustomersTable.tsx
//
// Lista paginada do segmento selecionado na tela "Ciclo de vida da base".
// `/customer-lifecycle/customers` pagina no servidor (skip/take), então esta
// tabela é um Table/TablePagination de mão em vez do Datagrid do react-admin
// (que exigiria o dataProvider genérico e o formato `filter=`, que este
// endpoint não usa — ver comentário em api.ts). CSV export segue a mesma regra
// de CPF mascarado do resto do admin (PartiallyHiddenCpfField/formatPartiallyHiddenCpf).
import React, { useEffect, useState } from 'react';
import { downloadCSV, useNotify, usePermissions } from 'react-admin';
// eslint-disable-next-line import/no-extraneous-dependencies
import jsonExport from 'jsonexport/dist';
import {
  Box, Button, Chip, CircularProgress, Skeleton, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TableSortLabel, Typography,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '../../constants';
import { formatCellphone, formatCpf, formatPartiallyHiddenCpf } from '../../utils';
import { canFetchLifecycle } from './lifecycleRange';
import {
  FetchCustomersOptions, LifecycleCustomer, LifecycleSegment, fetchLifecycleCustomers,
} from './api';

const EXPORT_CAP = 20000;
// Largura do shimmer de cada coluna enquanto carrega (Nome, CPF, Celular, Etiquetas,
// Último abastecimento, Dias sem abastecer, Cadastro, Campanha ativa).
const SKELETON_WIDTHS = [160, 110, 120, 70, 130, 40, 80, 90];
// Altura de uma linha com etiqueta (chip): o shimmer ocupa o mesmo espaço e a página não pula.
const ROW_HEIGHT = 41;
// CPF, celular, datas e números numa linha só; se faltar espaço, a tabela rola dentro dela.
const noWrap = { whiteSpace: 'nowrap' } as const;

interface Props {
  chainId: string;
  segment: LifecycleSegment;
  atRiskDays: number | null;
  inactiveDays: number | null;
  tagIds: string[];
}

type SortField = FetchCustomersOptions['order']['field'];

const SEGMENT_EXPORT_NAME: Record<LifecycleSegment, string> = {
  active: 'ativos',
  at_risk: 'em-risco',
  inactive: 'inativos',
  never: 'nunca-abasteceram',
};

const LifecycleCustomersTable: React.FC<Props> = ({ chainId, segment, atRiskDays, inactiveDays, tagIds }) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const isAdmin = !!permissions?.includes(PERMISSIONS.ADMIN);

  const [rows, setRows] = useState<LifecycleCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('lastFillinAt');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');

  // Volta pra primeira página sempre que o segmento ou os filtros mudam — senão a tela
  // pode ficar pedindo uma página que não existe mais no segmento novo.
  useEffect(() => { setPage(0); }, [segment, atRiskDays, inactiveDays, JSON.stringify(tagIds)]);

  useEffect(() => {
    if (!canFetchLifecycle(atRiskDays, inactiveDays)) { setLoading(false); return; }
    let canceled = false;
    setLoading(true);
    fetchLifecycleCustomers({
      chainId, segment, atRiskDays, inactiveDays, tagIds,
      skip: page * perPage, take: perPage,
      order: { field: sortField, direction: sortDir },
    })
      .then(({ data, total: t }) => { if (!canceled) { setRows(data); setTotal(t); } })
      .catch(() => { if (!canceled) { notify('Não foi possível carregar os clientes deste segmento.', { type: 'warning' }); } })
      .finally(() => { if (!canceled) { setLoading(false); } });
    return () => { canceled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, segment, atRiskDays, inactiveDays, JSON.stringify(tagIds), page, perPage, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortField(field);
      setSortDir('ASC');
    }
  };

  const cpfCell = (cpf: string | null) => {
    if (!cpf) return '—';
    return isAdmin ? formatCpf(cpf) : formatPartiallyHiddenCpf(cpf);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await fetchLifecycleCustomers({
        chainId, segment, atRiskDays, inactiveDays, tagIds,
        skip: 0, take: EXPORT_CAP,
        order: { field: sortField, direction: sortDir },
      });
      const csvRows = data.map((c) => ({
        nome: c.name,
        cpf: c.cpf ? formatPartiallyHiddenCpf(c.cpf) : '—',
        celular: c.cellphone ? formatCellphone(c.cellphone) : '—',
        etiquetas: (c.tags || []).map((t) => t.name).join(', '),
        ultimoAbastecimento: c.lastFillinAt ? dayjs(c.lastFillinAt).format('DD/MM/YYYY HH:mm') : 'Nunca abasteceu',
        diasSemAbastecer: c.daysWithoutFueling ?? '—',
        cadastro: dayjs(c.createdAt).format('DD/MM/YYYY'),
        campanhaAtiva: c.activeCampaign?.name || '—',
      }));
      jsonExport(csvRows, (_err: any, csv: string) => downloadCSV(csv, `ciclo-vida-${SEGMENT_EXPORT_NAME[segment]}`));
    } catch {
      notify('Não foi possível exportar. Tente de novo.', { type: 'warning' });
    } finally {
      setExporting(false);
    }
  };

  // `muiDir`/os valores finais calculados ANTES do JSX de propósito: um `as 'asc' | 'desc'`
  // dentro de um ternário que é atributo de JSX faz o plugin de build
  // scripts/i18n/babel-plugin-i18n-wrap.mjs (que embrulha texto de tela em __t()) descer
  // no tipo TS e tentar embrulhar o literal do TSLiteralType, quebrando o build
  // ("Property literal of TSLiteralType expected... CallExpression").
  const SortableHeader = ({ field, label }: { field: SortField; label: string }) => {
    const active = sortField === field;
    const muiDir: 'asc' | 'desc' = sortDir === 'ASC' ? 'asc' : 'desc';
    const cellSortDirection: 'asc' | 'desc' | false = active ? muiDir : false;
    const labelDirection: 'asc' | 'desc' = active ? muiDir : 'asc';
    return (
      <TableCell sortDirection={cellSortDirection}>
        <TableSortLabel active={active} direction={labelDirection} onClick={() => handleSort(field)}>
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          size="small"
          startIcon={exporting ? <CircularProgress size={14} /> : <GetAppIcon fontSize="small" />}
          onClick={handleExport}
          disabled={exporting || total === 0}
        >
          Exportar CSV
        </Button>
      </Box>
      {/* width 0 + minWidth 100%: a tabela rola dentro de si e não entra no fit-content do
          RaLayout. Sem isso a página inteira encolhia enquanto carregava e voltava a crescer
          quando as linhas chegavam. */}
      <TableContainer sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeader field="name" label="Nome" />
              <TableCell>CPF</TableCell>
              <TableCell>Celular</TableCell>
              <TableCell>Etiquetas</TableCell>
              <SortableHeader field="lastFillinAt" label="Último abastecimento" />
              <TableCell>Dias sem abastecer</TableCell>
              <SortableHeader field="createdAt" label="Cadastro" />
              <TableCell>Campanha ativa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Shimmer com o mesmo número de linhas da página anterior: a altura não pula.
              Array.from({ length: Math.min(rows.length || 10, perPage) }, (_, row) => (
                <TableRow key={row} sx={{ height: ROW_HEIGHT }}>
                  {SKELETON_WIDTHS.map((width, col) => (
                    <TableCell key={col}><Skeleton animation="wave" width={width} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">Nenhum cliente neste segmento.</Typography>
              </TableCell></TableRow>
            ) : rows.map((c) => (
              <TableRow
                key={c.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/customers/${c.id}/show`)}
              >
                <TableCell sx={{ minWidth: 180 }}>{c.name}</TableCell>
                <TableCell sx={noWrap}>{cpfCell(c.cpf)}</TableCell>
                <TableCell sx={noWrap}>{c.cellphone ? formatCellphone(c.cellphone) : '—'}</TableCell>
                <TableCell>
                  {(c.tags || []).map((t) => (
                    <Chip key={t.id} label={t.name} size="small" sx={{ mr: 0.5, mb: 0.5, bgcolor: t.color || undefined }} />
                  ))}
                  {!c.tags?.length && '—'}
                </TableCell>
                <TableCell sx={noWrap}>{c.lastFillinAt ? dayjs(c.lastFillinAt).format('DD/MM/YYYY HH:mm') : 'Nunca abasteceu'}</TableCell>
                <TableCell sx={noWrap}>{c.daysWithoutFueling ?? '—'}</TableCell>
                <TableCell sx={noWrap}>{dayjs(c.createdAt).format('DD/MM/YYYY')}</TableCell>
                <TableCell>
                  {c.activeCampaign ? (
                    <Chip
                      label={c.activeCampaign.name}
                      size="small"
                      color="info"
                      onClick={(e) => { e.stopPropagation(); navigate(`/customer-recovery-rules/${c.activeCampaign!.id}/show`); }}
                    />
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        // Mesma receita da tabela: a paginação tem ~600 px de largura mínima e alargava a página no celular.
        sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}
        count={total}
        page={page}
        rowsPerPage={perPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_e, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </Box>
  );
};

export default LifecycleCustomersTable;
